import { type StaticDecode, Type } from "typebox";
import {
  type FieldValue,
  LocString,
  Nullable,
  OptionalNullable,
  ReadonlyArray,
  ReadonlyInterface,
  ReadonlyObject,
} from "./schema";
import type { Sensitivity } from "./sensitivity";

export type RawHighlight = Record<string, FieldValue>;

interface Snippet {
  match?: string | null;
  context?: string;
}

export interface SearchHighlightMatch {
  value: FieldValue;
  snippets: readonly Snippet[];
}

export interface SearchHighlightNode {
  raw?: RawHighlight;
  matches?: Record<string, readonly SearchHighlight[]>;
  parent_sections?: Record<string, readonly SearchHighlightNode[]>;
}

export type SearchHighlight = SearchHighlightNode | SearchHighlightMatch;

export function isSearchHighlightMatch(
  m: SearchHighlight,
): m is SearchHighlightMatch {
  return "snippets" in m;
}

export function isSearchHighlightNode(
  m: SearchHighlight,
): m is SearchHighlightNode {
  return !isSearchHighlightMatch(m);
}

export const MongoEntity = ReadonlyObject({ _id: Type.String() });
export type MongoEntity = StaticDecode<typeof MongoEntity>;

export const PgEntity = ReadonlyObject({ id: Type.String() });
export type PgEntity = StaticDecode<typeof PgEntity>;

export const MongoAndPgEntity = ReadonlyInterface([MongoEntity, PgEntity], {});
export type MongoAndPgEntity = StaticDecode<typeof MongoAndPgEntity>;

const LabeledDescription = ReadonlyInterface([MongoAndPgEntity], {
  reference_project_id: OptionalNullable(Type.String()),
  label: LocString,
  value: LocString,
  origin_id: Nullable(Type.String()),
  disabled: Type.Optional(Type.Boolean()),
  version: Type.Integer(),
});
export type LabeledDescription = StaticDecode<typeof LabeledDescription>;

export interface CustomerContact extends LabeledDescription {
  name?: string;
  phone?: string;
  email?: string;
  quote?: LocString;
  notes?: LocString;
  quote_approver_name?: string;
  quote_approved_at?: string;
  quote_approver_id?: string;
  hide_quote?: boolean;
  contact_details_approver_name?: string;
  contact_details_approved_at?: string;
  contact_details_approver_id?: string;
  hide_contact_details?: boolean;
  name_approver_name?: string;
  name_approved_at?: string;
  name_approver_id?: string;
  order?: number;
  project_experience_id?: string;
  modifier_id?: string | null;
}

export const ImageUrl = ReadonlyObject({
  url: OptionalNullable(Type.String()),
});
export const Image = ReadonlyInterface([ImageUrl], {
  thumb: ImageUrl,
  fit_thumb: ImageUrl,
  large: ImageUrl,
  small_thumb: ImageUrl,
});
export type Image = StaticDecode<typeof Image>;

const CommonCustomTagCategory = ReadonlyInterface([MongoAndPgEntity], {
  values: LocString,
  can_be_used_for_cvs: Type.Boolean(),
  can_be_used_for_references: Type.Boolean(),
  can_be_used_for_customers: Type.Boolean(),
  allow_filtering: Type.Boolean(),
  lock_for_integrations: Type.Boolean(),
  external_unique_id: Nullable(Type.String()),
});

export const CustomTag = ReadonlyInterface([MongoAndPgEntity], {
  values: LocString,
  custom_tag_category_id: Type.String(),
  custom_tag_category: Type.Optional(CommonCustomTagCategory),
  category_ids: ReadonlyArray(Type.String()),
  external_unique_id: Nullable(Type.String()),
});

export type CustomTag = StaticDecode<typeof CustomTag>;

export const CustomTagCategory = ReadonlyInterface([CommonCustomTagCategory], {
  custom_tags: Type.Optional(ReadonlyArray(CustomTag)),
});

export type CustomTagCategory = StaticDecode<typeof CustomTagCategory>;

export type RangeFilterName =
  | "AreaRangeFilter"
  | "ExtentHoursNewRangeFilter"
  | "ExtentRangeFilter"
  | "TotalExtentHoursRangeFilter"
  | "TotalExtentRangeFilter"
  | string; // custom field base range filter

export type RangeFilter =
  | CurrencyAmtRange
  | AmtAndUnitRange
  | AmtRange
  | QuantityRange;

export interface QuantityRange {
  type: "quantityRange";
  enabled: boolean;
  name: RangeFilterName;
  label: string;
  amtField: string;
  amtFrom: number | null;
  amtTo: number | null;
  unitField: string;
  unitValue: string;
}

export interface CurrencyAmtRange {
  type: "currencyAmtRange";
  enabled: boolean;
  name: RangeFilterName;
  label: string;
  amtField: string;
  amtFrom: number | null;
  amtTo: number | null;
  unitField: string;
  unitValue: string;
}

export interface AmtAndUnitRange {
  type: "unitAmtRange";
  enabled: boolean;
  name: RangeFilterName;
  label: string;
  amtField: string;
  amtFrom: number | null;
  amtTo: number | null;
  unitField: string;
  unitValue: string;
}

export interface AmtRange {
  type: "amtRange";
  enabled: boolean;
  name: RangeFilterName;
  label: string;
  amtField: string;
  amtFrom: number | null;
  amtTo: number | null;
}

export const Office = ReadonlyInterface([MongoAndPgEntity], {
  // not sure if this can actually be null?
  name: Nullable(Type.String()),
  selected: Type.Boolean(),
  default_word_template_id: Nullable(Type.String()),
  default_word_json_template_id: Nullable(Type.String()),
  default_ppt_template_id: Nullable(Type.String()),
  default_indesign_template_id: Nullable(Type.String()),
  country_id: Type.String(),
  country_code: Type.String(),
  override_language_code: Nullable(Type.String()),
});
export type Office = StaticDecode<typeof Office>;

export const OfficeWithUserCounts = ReadonlyInterface([Office], {
  num_users: Type.Integer(),
  num_users_activated: Type.Integer(),
  num_users_deactivated: Type.Integer(),
});
export type OfficeWithUserCounts = StaticDecode<typeof OfficeWithUserCounts>;

const BaseCountry = ReadonlyInterface([MongoAndPgEntity], {
  code: Type.String(),
  native_language_code: Type.String(),
  override_ui_language_code: Nullable(Type.String()),
  selected: Type.Boolean(),
  default_ppt_template_id: Nullable(Type.String()),
  default_word_template_id: Nullable(Type.String()),
  default_word_json_template_id: Nullable(Type.String()),
  default_indesign_template_id: Nullable(Type.String()),
  setting: MongoEntity,
});
export const Country = ReadonlyInterface([BaseCountry], {
  offices: ReadonlyArray(Office),
});
export type Country = StaticDecode<typeof Country>;
export const CountryWithUserCounts = ReadonlyInterface([BaseCountry], {
  offices: ReadonlyArray(OfficeWithUserCounts),
});
export type CountryWithUserCounts = StaticDecode<typeof CountryWithUserCounts>;

export interface CvSearchResult extends MongoAndPgEntity {
  user_id: string;
  company_id: string;
  is_external: boolean;
  is_deactivated: boolean;
  image: Image;
  name: string;
  name_multilang: any;
  updated_at: string;
  updated_ago: string;
  owner_updated_at: string;
  title: string;
  titles: any;
  telephone: string;
  email: string;
  office_id: string;
  custom_tag_ids: readonly string[];
  country_id: string | null;
  country_code: string | null;
  language_code: string;
  language_codes: string[];
  default_indesign_template_id: string | null;
  default_ppt_template_id: string | null;
  default_word_template_id: string | null;
  default_word_json_template_id: string | null;
  template_document_type: string | null;
}

export interface CvsSearchResults {
  readonly total: number;
  readonly offset?: number;
  readonly cvs: readonly {
    readonly cv: CvSearchResult;
    readonly highlights: Record<string, readonly SearchHighlightNode[]>;
    readonly preview_url: string;
  }[];
}

export interface ReferenceSearchResult extends MongoAndPgEntity {
  company_customer_id: string;
  company_project_id: string;
  customer_name: LocString;
  project_name: LocString;
  image_count: number;
  preview_image_url: string | null;
  last_updated: string;
  is_deactivated: boolean;
  workflow_stage: string;
  month_from: string | null;
  year_from: string | null;
  month_to: string | null;
  year_to: string | null;
  project_name_sensitivity: Sensitivity;
  customer_sensitivity: Sensitivity;
}

interface WorkflowStageCounts {
  readonly all: number;
  readonly live: number;
  readonly draft: number;
  readonly ready_for_review: number;
}

export type WorkflowStage = keyof WorkflowStageCounts;

export interface ReferencesSearchResults {
  readonly total: number;
  readonly references: readonly {
    readonly reference: ReferenceSearchResult;
    readonly highlights: Record<string, readonly SearchHighlightNode[]>;
    readonly preview_url: string;
  }[];
  readonly workflow_stage_counts: WorkflowStageCounts;
}

export interface Company extends MongoEntity {
  /** @deprecated use `name` instead */
  navn: string;
  name: string;
  created_at: string;
  updated_at: string;
  updated_ago: string;
  cv_template_id: string | null;
  cv_template_type: string | null;
  default_ppt_template_id: string | null;
  default_word_template_id: string | null;
  default_word_json_template_id: string | null;
  default_indesign_template_id: string | null;
  google_apps_enabled: boolean;
  adfs_enabled: boolean;
  password_auth_enabled: boolean;
  part_of_group: boolean;
  setting: MongoEntity;
  language_codes: readonly string[];
  ui_language_codes: readonly string[];
}
